import { useState, useEffect, useCallback, useRef } from 'react';
import { salesCoachService, ChatMessage } from '../services/salesCoachService';
import { openaiService } from '../services/openaiService';
import {
  buildMeetingPrepContext,
  buildMeetingFeedbackContext,
  buildDealAdviceContext,
  buildLeadIntelContext,
  buildFollowUpStrategyContext,
  buildSalesScriptsContext,
  buildBaselineCompanyContext,
} from '../services/salesCoachContextService';
import { useTimezone } from './useTimezone';

interface UseSalesCoachProps {
  chatThreadId: string | null;
}

interface ContextInfo {
  contextType: string;
  contextParams: Record<string, string>;
  guideType?: string;
}

async function fetchContextByMeta(meta: ContextInfo, tz?: string): Promise<string> {
  const { contextType, contextParams } = meta;
  const { leadId, meetingId, dealId, transcript, scriptType } = contextParams;

  switch (contextType) {
    case 'meeting-prep':
      if (leadId && meetingId) return buildMeetingPrepContext(leadId, meetingId, tz);
      break;
    case 'meeting-feedback':
      if (leadId && meetingId) return buildMeetingFeedbackContext(leadId, meetingId, transcript, tz);
      break;
    case 'deal-advice':
      if (leadId && dealId) return buildDealAdviceContext(leadId, dealId, tz);
      break;
    case 'lead-intel':
      if (leadId) return buildLeadIntelContext(leadId, tz);
      break;
    case 'follow-up-strategy':
      if (leadId) return buildFollowUpStrategyContext(leadId, tz);
      break;
    case 'sales-scripts':
      if (scriptType) return buildSalesScriptsContext(scriptType, leadId, tz);
      break;
  }
  return '';
}

const REVEAL_INTERVAL_MS = 110;
const BUFFER_WORD_THRESHOLD = 80;

export function useSalesCoach({ chatThreadId }: UseSalesCoachProps) {
  const tz = useTimezone();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamingMessage, setStreamingMessage] = useState<string>('');
  const [isThinking, setIsThinking] = useState(false);
  const streamingRef = useRef('');
  const displayedRef = useRef('');
  const revealTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamDoneRef = useRef(false);
  const sessionContextRef = useRef<ContextInfo | null>(null);

  useEffect(() => {
    sessionContextRef.current = null;

    if (chatThreadId) {
      loadMessages();
      loadSessionContext();
    } else {
      setMessages([]);
    }

    const unsubscribe = salesCoachService.subscribeToMessages(chatThreadId, (newMessage) => {
      setMessages((prev) => {
        const exists = prev.some((m) => m.message_id === newMessage.message_id);
        if (exists) return prev;
        return [...prev, newMessage];
      });
    });

    return () => {
      unsubscribe();
    };
  }, [chatThreadId]);

  const loadMessages = async () => {
    if (!chatThreadId) {
      setMessages([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await salesCoachService.getSessionMessages(chatThreadId);
      setMessages(data);
    } catch (err) {
      setError('Failed to load chat history');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSessionContext = async () => {
    if (!chatThreadId) return;
    try {
      const meta = await salesCoachService.getSessionContextMeta(chatThreadId);
      if (meta) {
        sessionContextRef.current = {
          contextType: meta.context_type,
          contextParams: meta.context_params,
        };
      }
    } catch (err) {
      console.error('Failed to load session context:', err);
    }
  };

  const wordIndexRef = useRef(0);
  const wordsRef = useRef<string[]>([]);
  const renderFrameRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const splitIntoWords = (text: string): string[] => {
    const result: string[] = [];
    let i = 0;
    while (i < text.length) {
      if (/\s/.test(text[i])) {
        let end = i;
        while (end < text.length && /\s/.test(text[end])) end++;
        if (result.length > 0) {
          result[result.length - 1] += text.slice(i, end);
        } else {
          result.push(text.slice(i, end));
        }
        i = end;
      } else {
        let end = i;
        while (end < text.length && !/\s/.test(text[end])) end++;
        result.push(text.slice(i, end));
        i = end;
      }
    }
    return result;
  };

  const startRevealLoop = () => {
    if (revealTimerRef.current) return;
    displayedRef.current = '';
    streamDoneRef.current = false;
    wordIndexRef.current = 0;
    wordsRef.current = [];

    revealTimerRef.current = setInterval(() => {
      const full = streamingRef.current;
      wordsRef.current = splitIntoWords(full);
      const totalWords = wordsRef.current.length;
      const currentIdx = wordIndexRef.current;

      if (currentIdx >= totalWords) {
        if (streamDoneRef.current) {
          stopRevealLoop();
        }
        return;
      }

      const bufferedWords = totalWords - currentIdx;
      const wordsToReveal = bufferedWords > BUFFER_WORD_THRESHOLD
        ? Math.min(3, Math.ceil(bufferedWords / 20))
        : 1;

      const nextIdx = Math.min(currentIdx + wordsToReveal, totalWords);
      wordIndexRef.current = nextIdx;
      displayedRef.current = wordsRef.current.slice(0, nextIdx).join('');
    }, REVEAL_INTERVAL_MS);

    renderFrameRef.current = setInterval(() => {
      setStreamingMessage(displayedRef.current);
    }, 80);
  };

  const stopRevealLoop = () => {
    if (revealTimerRef.current) {
      clearInterval(revealTimerRef.current);
      revealTimerRef.current = null;
    }
    if (renderFrameRef.current) {
      clearInterval(renderFrameRef.current);
      renderFrameRef.current = null;
    }
  };

  const flushReveal = () => {
    stopRevealLoop();
    displayedRef.current = streamingRef.current;
    if (displayedRef.current) {
      setStreamingMessage(displayedRef.current);
    }
  };

  interface SendMessageOptions {
    contextType?: string;
    contextParams?: Record<string, string>;
    targetChatThreadId?: string;
    guideType?: string;
  }

  const sendMessage = useCallback(async (
    content: string,
    context?: string,
    sessionTitle?: string,
    options?: SendMessageOptions
  ) => {
    const effectiveThreadId = options?.targetChatThreadId || chatThreadId;

    if (!content.trim() || isSending || !effectiveThreadId) return;

    try {
      setIsSending(true);
      setIsThinking(true);
      setError(null);
      setStreamingMessage('');
      streamingRef.current = '';
      displayedRef.current = '';

      const isNewThread = options?.targetChatThreadId && options.targetChatThreadId !== chatThreadId;
      const isFirstMessage = isNewThread || messages.length === 0;
      const autoTitle = sessionTitle || (isFirstMessage ? content.trim().substring(0, 50) : undefined);

      if (options?.contextType && options?.contextParams) {
        sessionContextRef.current = {
          contextType: options.contextType,
          contextParams: options.contextParams,
          guideType: options.guideType,
        };
      }

      const messagePayload: any = {
        role: 'user' as const,
        content: content.trim(),
        chat_thread_id: effectiveThreadId,
        session_title: autoTitle,
      };

      if (isFirstMessage && options?.contextType && options?.contextParams) {
        messagePayload.context_type = options.contextType;
        messagePayload.context_params = options.contextParams;
      }

      const userMessage = await salesCoachService.createMessage(messagePayload);

      setMessages((prev) => {
        const exists = prev.some((m) => m.message_id === userMessage.message_id);
        if (exists) return prev;
        return [...prev, userMessage];
      });

      if (!openaiService.isConfigured()) {
        setError('OpenAI API key is not configured. Please add VITE_OPENAI_API_KEY to your .env file.');
        setIsSending(false);
        return;
      }

      let resolvedContext = context || '';
      if (!resolvedContext && sessionContextRef.current) {
        try {
          resolvedContext = await fetchContextByMeta(sessionContextRef.current, tz);
        } catch (err) {
          console.error('Failed to re-fetch context:', err);
        }
      }

      if (!resolvedContext) {
        try {
          resolvedContext = await buildBaselineCompanyContext();
        } catch (err) {
          console.error('Failed to fetch baseline company context:', err);
        }
      }

      const chatHistory: { role: 'user' | 'assistant' | 'system'; content: string }[] = [];

      if (resolvedContext) {
        chatHistory.push({
          role: 'user',
          content: `[CRM CONTEXT - Use this data to provide specific, personalized advice. Do not repeat this data verbatim, instead reference it naturally in your response.]\n\n${resolvedContext}`,
        });
        chatHistory.push({
          role: 'assistant',
          content: 'I have all the CRM context loaded. I\'ll now analyze this data and provide my full strategic assessment.',
        });
      }

      messages.forEach((msg) => {
        chatHistory.push({
          role: msg.role as 'user' | 'assistant',
          content: msg.content,
        });
      });

      chatHistory.push({
        role: 'user',
        content: content.trim(),
      });

      const resolvedGuideType = options?.guideType || sessionContextRef.current?.guideType;

      startRevealLoop();

      let firstChunkReceived = false;
      await openaiService.getChatCompletion(chatHistory, (fullText) => {
        if (!firstChunkReceived) {
          firstChunkReceived = true;
          setIsThinking(false);
        }
        streamingRef.current = fullText;
      }, resolvedGuideType);

      streamDoneRef.current = true;

      await new Promise<void>((resolve) => {
        const check = setInterval(() => {
          if (displayedRef.current.length >= streamingRef.current.length) {
            clearInterval(check);
            resolve();
          }
        }, REVEAL_INTERVAL_MS);
      });

      flushReveal();

      const fullResponse = streamingRef.current;
      const resolvedTitle = autoTitle || userMessage.session_title;

      const assistantMessage = await salesCoachService.createMessage({
        role: 'assistant',
        content: fullResponse,
        chat_thread_id: effectiveThreadId,
        session_title: resolvedTitle,
      });

      if (!resolvedTitle && isFirstMessage) {
        const fallbackTitle = content.trim().substring(0, 50);
        if (fallbackTitle) {
          try {
            await salesCoachService.updateSessionTitle(effectiveThreadId, fallbackTitle);
          } catch {}
        }
      }

      setMessages((prev) => {
        const exists = prev.some((m) => m.message_id === assistantMessage.message_id);
        if (exists) return prev;
        return [...prev, assistantMessage];
      });

      setStreamingMessage('');
    } catch (err: any) {
      flushReveal();
      setStreamingMessage('');
      setIsThinking(false);
      setError(err.message || 'Failed to send message');
      console.error(err);
    } finally {
      setIsSending(false);
    }
  }, [messages, isSending, chatThreadId]);

  const clearChat = useCallback(async () => {
    if (!chatThreadId) return;

    try {
      setError(null);
      await salesCoachService.deleteSession(chatThreadId);
      setMessages([]);
      sessionContextRef.current = null;
    } catch (err) {
      setError('Failed to clear chat');
      console.error(err);
    }
  }, [chatThreadId]);

  return {
    messages,
    isLoading,
    isSending,
    isThinking,
    error,
    streamingMessage,
    sendMessage,
    clearChat,
    refreshMessages: loadMessages,
  };
}
